import { useDashboard } from "../../../../context/dashboardContext";
import { useState, useMemo } from "react";
import ConfirmModal from '../../../../components/shareable/confirmModal';
import { MoreVertical, UserCheck } from 'lucide-react';
import ResetPasswordModal from './resetPasswordModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, } from '../../../../components/shareable/dropdownMenu';
import { Pagination, PaginationContent, PaginationNext, PaginationPrevious, } from '../../../../components/shareable/pagination';
import toast from 'react-hot-toast';

const TableComponent = ({ data = [], refreshData }) => {
  const { deleteUser, updateUserPassword } = useDashboard();
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectRow, setSelectRow] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Current user's email - hardcoded as per requirement
  const currentUserEmail = "rrose@babsusa.com";

  const totalPages = useMemo(() => Math.ceil(data.length / itemsPerPage), [data]);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, data]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 bg-white">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Index
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Joined Date
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        
        <tbody className="bg-white divide-y divide-gray-200">
          {paginatedData.length > 0 ? (
            paginatedData.map((row, rowIndex) => {
              const isCurrentUser = row.Email === currentUserEmail;
              return (
                <tr key={rowIndex} className={`hover:bg-gray-50 transition-colors ${isCurrentUser ? 'bg-red-50 border-l-4 border-red-500' : ''}`} >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {currentPage * 10 + (rowIndex - 9)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    <div className="flex items-center justify-center">
                      {row.Client || <span className="italic text-gray-400">N/A</span>}
                      {isCurrentUser && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <UserCheck className="w-3 h-3 mr-1" /> You
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {row.Email || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {row.Date || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger className="focus:outline-none">
                        <MoreVertical className="h-5 w-5 text-gray-500 hover:text-red-600 transition-colors mx-auto" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-44 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                        <DropdownMenuLabel className="px-3 py-2 text-xs font-medium text-gray-500 text-center">
                          Actions
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => { setSelectRow(row); setResetOpen(true); }}
                          className="cursor-pointer px-3 py-2 text-sm text-yellow-600 hover:bg-yellow-50 focus:bg-yellow-50"
                        >
                          Reset Password
                        </DropdownMenuItem>
                        {!isCurrentUser && (
                          <DropdownMenuItem
                            onClick={() => { setSelectRow(row); setDeleteOpen(true); }}
                            className="cursor-pointer px-3 py-2 text-sm text-red-600 hover:bg-red-50 focus:bg-red-50"
                          >
                            Delete User
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                <div className="flex flex-col items-center justify-center">
                  <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  No users available
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {data.length > 10 && (
        <div className="flex justify-between items-center mt-6 px-6">
          <strong className="text-sm text-gray-500">
            Showing {paginatedData.length} of {data.length} users
          </strong>
          <Pagination>
            <PaginationContent>
              <PaginationPrevious
                className={`${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'} border border-gray-300 px-3 py-1 rounded-md text-sm`}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              <span className="mx-4 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <PaginationNext
                className={`${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'} border border-gray-300 px-3 py-1 rounded-md text-sm`}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <ConfirmModal
        onConfirm={async () => {
          const res = await deleteUser(selectRow);
          if (res.success) {
            toast.success('User deleted successfully');
            refreshData();
          } else {
            toast.error('Failed to delete user');
          }
          setDeleteOpen(false);
        }}
        title="Confirm Deletion"
        description="Are you sure you want to delete this user?"
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
      />

      <ResetPasswordModal
        isOpen={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={async (password) => {
          const res = await updateUserPassword(selectRow, password);
          if (res.success) {
            toast.success('Password updated successfully');
          } else {
            toast.error('Failed to update password');
          }
          setResetOpen(false);
        }}
        userEmail={selectRow?.Email}
      />
    </div>
  );
};

export default TableComponent;
